/**
 * RollbackModal - Fase 7: Incluye Vista de Diff
 * Permite seleccionar un snapshot, ver cambios y confirmar la restauración.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, RotateCcw, Clock, AlertTriangle, Check, Loader2, Tag, GitCompare, Search, FileCode, MessageSquare } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import SnapshotDiffViewer from './Snapshots/SnapshotDiffViewer';

import { API_URL } from '../config';

const RollbackModal = ({ isOpen, onClose, onRollbackComplete }) => {
  const { language } = useLanguage();
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDiff, setShowDiff] = useState(false);

  // Fase 11.4: filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [sortMode, setSortMode] = useState('newest'); // newest | oldest | summary_az

  // Traducciones
  const t = {
    title: language === 'es' ? 'Restaurar Snapshot' : 'Restore Snapshot',
    subtitle: language === 'es' ? 'Selecciona un punto de restauración' : 'Select a restore point',
    confirmTitle: language === 'es' ? '¿Confirmar restauración?' : 'Confirm restoration?',
    confirmText: language === 'es'
      ? 'Esto reemplazará el estado actual por el snapshot seleccionado. Esta acción puede afectar tu trabajo actual.'
      : 'This will replace the current state with the selected snapshot. This action may affect your current work.',
    cancel: language === 'es' ? 'Cancelar' : 'Cancel',
    restore: language === 'es' ? 'Restaurar' : 'Restore',
    restoring: language === 'es' ? 'Restaurando...' : 'Restoring...',
    selectSnapshot: language === 'es' ? 'Selecciona un snapshot' : 'Select a snapshot',
    noSnapshots: language === 'es' ? 'No hay snapshots disponibles' : 'No snapshots available',
    loading: language === 'es' ? 'Cargando snapshots...' : 'Loading snapshots...',
    success: language === 'es' ? 'Estado restaurado correctamente' : 'State restored successfully',
    error: language === 'es' ? 'Error al restaurar' : 'Error restoring',
    selected: language === 'es' ? 'Seleccionado' : 'Selected',
    viewChanges: language === 'es' ? 'Ver cambios' : 'View changes',
    // Fase 11.4
    searchPh: language === 'es' ? 'Buscar por resumen o tag...' : 'Search by summary or tag...',
    sortNewest: language === 'es' ? 'Más recientes' : 'Newest',
    sortOldest: language === 'es' ? 'Más antiguos' : 'Oldest',
    sortAZ: language === 'es' ? 'A-Z por resumen' : 'A-Z by summary',
    countLabel: language === 'es' ? 'snapshots' : 'snapshots',
    of: language === 'es' ? 'de' : 'of',
    all: language === 'es' ? 'Todos' : 'All',
    previewTitle: language === 'es' ? 'Vista previa' : 'Preview',
    previewEmpty: language === 'es' ? 'Selecciona un snapshot para ver detalles' : 'Select a snapshot to see details',
    modulesCount: language === 'es' ? 'Módulos' : 'Modules',
    convsCount: language === 'es' ? 'Conversaciones' : 'Conversations',
    tagsLabel: language === 'es' ? 'Tags' : 'Tags',
    snapshotId: language === 'es' ? 'ID' : 'ID',
    summaryLabel: language === 'es' ? 'Resumen' : 'Summary',
    dateLabel: language === 'es' ? 'Fecha' : 'Date',
    noResults: language === 'es' ? 'Sin resultados con estos filtros' : 'No results with these filters',
  };

  // Cargar snapshots al abrir el modal
  useEffect(() => {
    if (isOpen) {
      fetchSnapshots();
      setSelectedSnapshotId(null);
      setShowConfirm(false);
      setSearchTerm('');
      setSelectedTag(null);
      setSortMode('newest');
    }
  }, [isOpen]);

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      // CUADRO NEGRO: cookie-only auth (withCredentials).
      const response = await fetch(`${API_URL}/api/snapshots/`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setSnapshots(data);
    } catch (err) {
      console.error('Error fetching snapshots:', err);
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSnapshot = (snapshotId) => {
    setSelectedSnapshotId(snapshotId);
    setShowConfirm(false);
  };

  const handleProceedToConfirm = () => {
    if (!selectedSnapshotId) {
      toast.warning(t.selectSnapshot);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmRollback = async () => {
    if (!selectedSnapshotId) return;

    // Cachear el summary del snapshot seleccionado ANTES de restaurar
    // (Fase 11.3: lo usamos para construir un toast enriquecido).
    const restoredSummary = selectedSnapshot?.summary || '';

    setRestoring(true);
    try {
      // SUGERENCIA: capturar snapshot pre-restore para poder "Deshacer".
      // Si falla (red, permisos), seguimos con el restore — el banner de
      // Deshacer simplemente no aparecerá.
      let undoSnapshotId = null;
      try {
        const preSnapRes = await fetch(`${API_URL}/api/snapshots/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            summary: language === 'es'
              ? `Pre-restauración (auto) — ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`
              : `Pre-restore (auto) — ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`,
            tags: ['pre-restore', 'auto'],
          }),
        });
        if (preSnapRes.ok) {
          const preSnapData = await preSnapRes.json();
          undoSnapshotId = preSnapData?.id || preSnapData?.snapshot?.id || null;
        }
      } catch (_) { /* no rompemos el flujo si falla */ }

      // CUADRO NEGRO: cookie-only (withCredentials). NO Authorization header.
      const response = await fetch(`${API_URL}/api/snapshots/${selectedSnapshotId}/restore`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.ok) {
        // Fase 11.2: notificar al resto de la app que se restauró un snapshot.
        // App.js limpia currentConversation (evita GET stale → toast falso
        // 'Conversación no encontrada') y recarga la lista. InternalWorkspace
        // refresca su módulo abierto.
        try {
          window.dispatchEvent(new CustomEvent('damyxia:snapshot-restored', {
            detail: {
              snapshotId: selectedSnapshotId,
              summary: restoredSummary,
              timestamp: data.snapshot_timestamp || '',
              undoSnapshotId, // mini barra "Deshacer" aparecerá si !== null
            },
          }));
        } catch (_) { /* noop */ }

        // Fase 11.3: mensaje de éxito enriquecido con el summary del snapshot.
        const successText = restoredSummary
          ? (language === 'es'
              ? `✓ Snapshot restaurado: ${restoredSummary}`
              : `✓ Snapshot restored: ${restoredSummary}`)
          : t.success;
        toast.success(successText, { duration: 5000 });

        onClose();
        if (onRollbackComplete) {
          onRollbackComplete(data);
        }
      } else {
        toast.error(`${t.error}: ${data.message}`);
      }
    } catch (err) {
      console.error('Error restoring snapshot:', err);
      toast.error(t.error);
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '--';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const selectedSnapshot = snapshots.find(s => s.id === selectedSnapshotId);

  // ─── Fase 11.4: tags autodescubiertos + filtrado + orden ─────────────
  const allTags = useMemo(() => {
    const set = new Set();
    snapshots.forEach((s) => (s.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [snapshots]);

  const filteredSnapshots = useMemo(() => {
    let list = snapshots.slice();
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const summary = (s.summary || '').toLowerCase();
        const tagsStr = (s.tags || []).join(' ').toLowerCase();
        return summary.includes(q) || tagsStr.includes(q);
      });
    }
    if (selectedTag) {
      list = list.filter((s) => (s.tags || []).includes(selectedTag));
    }
    if (sortMode === 'newest') {
      list.sort((a, b) => (new Date(b.timestamp) - new Date(a.timestamp)));
    } else if (sortMode === 'oldest') {
      list.sort((a, b) => (new Date(a.timestamp) - new Date(b.timestamp)));
    } else if (sortMode === 'summary_az') {
      list.sort((a, b) => (a.summary || '').localeCompare(b.summary || ''));
    }
    return list;
  }, [snapshots, searchTerm, selectedTag, sortMode]);

  // Conteos del snapshot seleccionado para el mini-preview (sin tocar backend).
  const previewStats = useMemo(() => {
    if (!selectedSnapshot) return null;
    const data = selectedSnapshot.data || {};
    const ws = data.workspace_code || {};
    let modulesCount = 0;
    if (Array.isArray(ws)) {
      modulesCount = ws.length;
    } else if (typeof ws === 'object' && ws !== null) {
      // workspace_code puede ser dict directo {moduleId: moduleObj} (formato
      // moderno) o {modules: [...]} (legacy). Toleramos ambos y validamos que
      // los values sean módulos reales (con name, code o id).
      if (Array.isArray(ws.modules)) {
        modulesCount = ws.modules.length;
      } else if (ws.modules && typeof ws.modules === 'object') {
        modulesCount = Object.keys(ws.modules).length;
      } else {
        modulesCount = Object.keys(ws).filter((k) => {
          const v = ws[k];
          return v && typeof v === 'object' && (v.name || v.code || v.id);
        }).length;
      }
    }
    const ch = data.chat_history || [];
    const convsCount = Array.isArray(ch) ? ch.length : 0;
    return { modulesCount, convsCount, isEmpty: modulesCount === 0 };
  }, [selectedSnapshot]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg md:max-w-4xl mx-4 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden" data-testid="rollback-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <RotateCcw className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{t.title}</h2>
              <p className="text-xs text-zinc-500">{t.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-zinc-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {showConfirm ? (
            /* Vista de confirmación */
            <div className="text-center max-w-md mx-auto">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{t.confirmTitle}</h3>
              <p className="text-sm text-zinc-400 mb-4">{t.confirmText}</p>

              {/* Snapshot seleccionado */}
              {selectedSnapshot && (
                <div className="bg-zinc-800/50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-white font-medium">{selectedSnapshot.summary}</p>
                  <p className="text-xs text-zinc-500 mt-1">{formatDate(selectedSnapshot.timestamp)}</p>
                  <p className="text-xs text-zinc-600 mt-1 font-mono">{selectedSnapshot.id}</p>
                </div>
              )}

              {/* Botones de confirmación */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={restoring}
                  className="flex-1 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors disabled:opacity-50"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={handleConfirmRollback}
                  disabled={restoring}
                  data-testid="rollback-confirm-btn"
                  className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {restoring ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t.restoring}</span>
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      <span>{t.restore}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Vista de selección de snapshot */
            <>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-zinc-600 animate-spin mb-3" />
                  <p className="text-sm text-zinc-500">{t.loading}</p>
                </div>
              ) : snapshots.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Clock className="w-8 h-8 text-zinc-600 mb-3" />
                  <p className="text-sm text-zinc-500">{t.noSnapshots}</p>
                </div>
              ) : (
                <>
                  {/* Filtros (Fase 11.4) */}
                  <div className="space-y-2 mb-3">
                    <div className="flex flex-col md:flex-row gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder={t.searchPh}
                          data-testid="rollback-search-input"
                          className="w-full pl-9 pr-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500/60"
                        />
                      </div>
                      <select
                        value={sortMode}
                        onChange={(e) => setSortMode(e.target.value)}
                        data-testid="rollback-sort-select"
                        className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:border-amber-500/60"
                      >
                        <option value="newest">{t.sortNewest}</option>
                        <option value="oldest">{t.sortOldest}</option>
                        <option value="summary_az">{t.sortAZ}</option>
                      </select>
                    </div>
                    {allTags.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap" data-testid="rollback-tag-chips">
                        <button
                          onClick={() => setSelectedTag(null)}
                          className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                            !selectedTag
                              ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                              : 'bg-zinc-800/50 text-zinc-400 border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          {t.all}
                        </button>
                        {allTags.map((tag) => (
                          <button
                            key={tag}
                            onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                            className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                              selectedTag === tag
                                ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                                : 'bg-zinc-800/50 text-indigo-300 border-zinc-700 hover:border-zinc-600'
                            }`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-zinc-500" data-testid="rollback-count-label">
                      {filteredSnapshots.length} {t.of} {snapshots.length} {t.countLabel}
                    </p>
                  </div>

                  {/* Split: Lista (izq) + Preview (der, solo desktop) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Lista de snapshots */}
                    <div className="max-h-[360px] overflow-y-auto space-y-2 pr-1" data-testid="rollback-snapshot-list">
                      {filteredSnapshots.length === 0 ? (
                        <div className="text-center py-8 text-xs text-zinc-500">{t.noResults}</div>
                      ) : filteredSnapshots.map((snapshot) => (
                        <div
                          key={snapshot.id}
                          onClick={() => handleSelectSnapshot(snapshot.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedSnapshotId === snapshot.id
                              ? 'bg-amber-500/10 border-amber-500/50'
                              : 'bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600/50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm text-white font-medium truncate">{snapshot.summary}</p>
                                {selectedSnapshotId === snapshot.id && (
                                  <span className="px-2 py-0.5 text-[10px] bg-amber-500/20 text-amber-400 rounded-full flex items-center gap-1 shrink-0">
                                    <Check className="w-3 h-3" />
                                    {t.selected}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="w-3 h-3 text-zinc-500" />
                                <span className="text-xs text-zinc-500">{formatDate(snapshot.timestamp)}</span>
                              </div>
                              {snapshot.tags && snapshot.tags.length > 0 && (
                                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                  <Tag className="w-3 h-3 text-zinc-500" />
                                  {snapshot.tags.map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-0.5 text-[10px] bg-indigo-500/20 text-indigo-400 rounded-full"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Mini-preview (Fase 11.4) */}
                    <div className="hidden md:block bg-zinc-800/30 border border-zinc-800 rounded-lg p-4 max-h-[360px] overflow-y-auto" data-testid="rollback-preview-pane">
                      <div className="flex items-center gap-2 mb-3">
                        <FileCode className="w-4 h-4 text-amber-400" />
                        <h4 className="text-sm font-semibold text-white">{t.previewTitle}</h4>
                      </div>
                      {!selectedSnapshot ? (
                        <p className="text-xs text-zinc-500 text-center py-8">{t.previewEmpty}</p>
                      ) : (
                        <div className="space-y-3 text-xs">
                          <div>
                            <p className="text-zinc-400 mb-1">{t.summaryLabel}</p>
                            <p className="text-white">{selectedSnapshot.summary}</p>
                          </div>
                          <div>
                            <p className="text-zinc-400 mb-1">{t.dateLabel}</p>
                            <p className="text-white">{formatDate(selectedSnapshot.timestamp)}</p>
                          </div>
                          <div>
                            <p className="text-zinc-400 mb-1">{t.snapshotId}</p>
                            <p className="text-white font-mono text-[10px] break-all">{selectedSnapshot.id}</p>
                          </div>
                          {selectedSnapshot.tags && selectedSnapshot.tags.length > 0 && (
                            <div>
                              <p className="text-zinc-400 mb-1">{t.tagsLabel}</p>
                              <div className="flex flex-wrap gap-1">
                                {selectedSnapshot.tags.map((tag, idx) => (
                                  <span key={idx} className="px-1.5 py-0.5 text-[10px] bg-indigo-500/20 text-indigo-400 rounded-full">{tag}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {previewStats && previewStats.isEmpty && (
                            <div className="bg-red-500/15 border border-red-500/40 rounded p-2 text-[11px] text-red-300" data-testid="rollback-empty-banner">
                              ⚠️ {language === 'es'
                                ? 'Este snapshot no contiene módulos. No se puede restaurar.'
                                : 'This snapshot has no modules. Restore is disabled.'}
                            </div>
                          )}
                          {previewStats && (
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-800">
                              <div className={`rounded p-2 flex items-center gap-2 ${previewStats.isEmpty ? 'bg-red-500/10' : 'bg-zinc-900/50'}`}>
                                <FileCode className={`w-3.5 h-3.5 ${previewStats.isEmpty ? 'text-red-400' : 'text-cyan-400'}`} />
                                <div>
                                  <p className="text-zinc-500 text-[10px] uppercase">{t.modulesCount}</p>
                                  <p className={`font-semibold ${previewStats.isEmpty ? 'text-red-400' : 'text-white'}`}>{previewStats.modulesCount}</p>
                                </div>
                              </div>
                              <div className="bg-zinc-900/50 rounded p-2 flex items-center gap-2">
                                <MessageSquare className="w-3.5 h-3.5 text-purple-400" />
                                <div>
                                  <p className="text-zinc-500 text-[10px] uppercase">{t.convsCount}</p>
                                  <p className="text-white font-semibold">{previewStats.convsCount}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Botones de acción */}
                  <div className="flex gap-3 mt-4">
                    {/* Botón Ver Cambios - Fase 7 */}
                    <button
                      onClick={() => selectedSnapshotId && setShowDiff(true)}
                      disabled={!selectedSnapshotId}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        selectedSnapshotId
                          ? 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30'
                          : 'bg-zinc-800 text-zinc-500 cursor-not-allowed border border-zinc-700'
                      }`}
                    >
                      <GitCompare className="w-4 h-4" />
                      <span>{t.viewChanges}</span>
                    </button>

                    {/* Botón Restaurar */}
                    <button
                      onClick={handleProceedToConfirm}
                      disabled={!selectedSnapshotId || (previewStats && previewStats.isEmpty)}
                      data-testid="rollback-restore-btn"
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        selectedSnapshotId && !(previewStats && previewStats.isEmpty)
                          ? 'bg-amber-500 hover:bg-amber-400 text-black'
                          : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                      }`}
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>{selectedSnapshotId ? t.restore : t.selectSnapshot}</span>
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de Diff - Fase 7 */}
      {showDiff && selectedSnapshotId && (
        <SnapshotDiffViewer
          snapshotId={selectedSnapshotId}
          onClose={() => setShowDiff(false)}
        />
      )}
    </div>
  );
};

export default RollbackModal;
